import { getExample, parsePhoneNumber, getCountryCodeForRegionCode } from 'awesome-phonenumber';

import BaseModel from '@core/model';

import i18n from '@plugins/i18n';

const PossibilitiesEnum = Object.freeze({
    IS_POSSIBLE: 'is-possible',
    TOO_LONG: 'too-long',
    TOO_SHORT: 'too-short',
    INVALID_COUNTRY_CODE: 'invalid-country-code',
    UNKNOWN: 'unknown'
});

export class PhoneNumber extends BaseModel {
    static entity = 'phoneNumbers';
    static runSetup = true;

    static fields() {
        return {
            ...super.fields(),
            error: this.string(null).nullable(),
            input: this.string(null).nullable(),
            country: this.string(null).nullable(), // Alpha2Code
            calling_code: this.number(null).nullable(),
            number: this.string(null).nullable(),
            e164: this.string(null).nullable(),
            type_id: this.string(null).nullable(),
            number_type: this.attr(null).nullable()
        };
    }

    async setupLogic() {
        if (!this.$setupLoaded && this.number && !this.input) {
            this.value = this.number;
        }
    }

    get example() {
        try {
            return getExample(this.country || 'GB').number.international;
        } catch (error) {
            return null;
        }
    }

    get value() {
        return this.input || this.number;
    }
    set value(input) {
        const parsed = this.parseNumber(input || '');

        for (const field in parsed) {
            this[`${field}`] = parsed[`${field}`];
        }
    }

    get country_code() {
        return this.country;
    }
    set country_code(country_code) {
        const previousCallingCode = this.calling_code || getCountryCodeForRegionCode(this.country);

        this.country = country_code;
        this.calling_code = getCountryCodeForRegionCode(country_code);

        if (this.value && this.value.startsWith('+') && previousCallingCode) {
            this.input = this.value.replace(`+${previousCallingCode}`, `+${this.calling_code}`);
            this.value = this.input;
        }
    }

    get error_messages() {
        if (this.error) {
            return [this.error];
        }

        return null;
    }

    get is_valid() {
        return [this.number, this.country, this.number_type].every(v => v && v.length > 0);
    }

    get computed_name() {
        let name = '';

        if (this.value) {
            name += this.value;
        }

        if (this.number_type) {
            name += ' - ' + this.number_type;
        }

        if (this.number && name) {
            return name;
        }

        return i18n.t('phone_number');
    }

    parseNumber(input, field = i18n.t('value')) {
        const result = {
            error: null,
            calling_code: this.calling_code,
            country: this.country,
            number: null,
            e164: null,
            input
        };

        if (!input || typeof input !== 'string') {
            result.error = i18n.t('validator.phone_number', { field });
            return result;
        }

        if (input.length < 7) {
            result.error = i18n.t('validator.min_items', { field, minimum: 7, items: 'digits' });
            return result;
        }

        if (input.length > 20) {
            result.error = i18n.t('validator.max_items', { field, maximum: 20, items: 'digits' });
            return result;
        }

        const parsed = parsePhoneNumber(input, {
            regionCode: this.country
        });

        if (!parsed.possible && parsed.possibility === PossibilitiesEnum.UNKNOWN) {
            result.error = i18n.t('validator.phone_number', { field });
        }

        if (!parsed.possible && parsed.possibility === PossibilitiesEnum.TOO_LONG) {
            result.error = i18n.t('validator.too_long', { field, items: 'digits' });
        }

        if (!parsed.possible && parsed.possibility === PossibilitiesEnum.TOO_SHORT) {
            result.error = i18n.t('validator.too_short', { field, items: 'digits' });
        }

        if (!parsed.valid) {
            result.error = i18n.t('validator.phone_number', { field });
        }

        if (result.error) {
            return result;
        }

        result.calling_code = parsed.countryCode;
        result.country = parsed.regionCode;
        result.number = parsed.number.significant;
        result.e164 = parsed.number.e164;
        result.input = parsed.number.international;

        return result;
    }
}

export default PhoneNumber;
